// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import { RouteNames } from 'router';
import RenderSEO from 'components/atoms/renderSEO';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const ContactPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    footerLink,
    getFooterLinkData,
    siteSettings,
    contact,
    getContactDetails,
  } = props;

  const metaTagSEO = {
    metaTitle: 'Contact Us | Total Environment Homes',
    metaDescription:
      'In order to contact us, fill in your details and submit the form. Our Total Environment team will connect with you soon.',
    metaKeywords: '',
  };

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState('light-theme');

  useEffect(() => {
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getFooterLinkData();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (!contact || !siteSettings || !verticals || !footerLink) {
    return null;
  }

  const componentList = [
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: RouteNames.contact,
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Contacts',
          breadcrumbLink: RouteNames.contact,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.getContactsLoading(state) ||
      HomeSelector.getVerticalsLoading(state) ||
      FooterSelector.isLoading(state) ||
      HomeSelector.getSiteSettingsLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    siteSettings: HomeSelector.getSiteSettings(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { getContactDetails, getVerticals, getSiteSettings } = HomeActions;
  const { getFooterLinkData } = FooterActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getFooterLinkData,
      getSiteSettings,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(ContactPage);
